import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { Vendor } from 'app/models/entities/vendors/vendor';
import { VendorCustomField } from '../../../../models/entities/vendors/vendor-custom-field';
import { ImageUploadComponent, ImageUploadDialogComponent } from '../../../v2-layout/shared/uploaders';
import { VendorService } from '../../../../services/billing/vendor.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { BankAccountInsightsService } from '../../../../services/financial-accounts/bank-account-insights.service';
import { ConfigGeoRegions } from '../../../../config/geo-regions';
import customPatternValidator from '../../../../directives/validators/custom-pattern.validator';
import conditionalValidator from '../../../../directives/validators/conditional.validator';
import { OrganizationService } from '../../../../services/organization/organization.service';
import { Organization } from '../../../../models/entities/organization';
import { CreateVendorBankAccountRequest, VendorBankAccountsService } from '../../../../services/stripe-treasury/vendor-bank-accounts.service';
import { SharedModule } from '../../../shared/shared.module';
import { GeoRegionSelectorComponent } from '../../../shared/geo-region-selector/geo-region-selector.component';
import { NgxMaskDirective } from 'ngx-mask';
import { RequiredMessageComponent } from '../../shared/required-message/required-message.component';
import { ControlMessagesComponent } from '../../../shared/control-messages/control-messages.component';
import { CategoryPickerComponent } from '../../../shared/category-picker/category-picker.component';
import { AddressSuggestionRow, AddressSuggestions } from '../../../../services/calendar/address-suggestions';
import { CustomFieldFilterPipe, CustomFieldSearchComponent } from '../../../shared/custom-field-search/custom-field-search.component';

/**
 * Manages vendor creation and editing with Lob check printing constraints.
 *
 * Enforces Lob API address requirements: 40-char name limit, PO Box handling rules,
 * and combined address length restrictions. Supports both standalone routing and
 * embedded dialog usage. Vendor categorization determines transaction posting
 * behavior - asset/liability/equity vendors post to balance sheet accounts while
 * expense vendors post to P&L categories.
 */
@UntilDestroy()
@Component({
    selector: 'app-vendor-editor',
    templateUrl: './vendor-editor.component.html',
    standalone: true,
    imports: [
        SharedModule,
        GeoRegionSelectorComponent,
        ImageUploadComponent,
        ImageUploadDialogComponent,
        NgxMaskDirective,
        RequiredMessageComponent,
        RouterModule,
        ControlMessagesComponent,
        CategoryPickerComponent,
        CustomFieldSearchComponent,
        CustomFieldFilterPipe,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VendorEditorComponent implements OnInit
{
    readonly accountTypeItems = [
        { value: 'checking', label: 'Checking' },
        { value: 'savings', label: 'Savings' },
    ];

    @Input() embedded = false;
    @Output() saved: EventEmitter<Vendor> = new EventEmitter<Vendor>();

    // vendor info
    @Input() vendorId = 0;
    vendor: Vendor = null;
    containsPOBox: RegExp = /(P\.?\s*O\.?\s+Box|post\s+office\s+box|p\s*o\s+box)\s\w+/i;

    // custom fields
    customFields: VendorCustomField[] = [];
    customFieldSearch = '';
    readonly customFieldName = (field: VendorCustomField) => field.name;
    readonly customFieldValue = (field: VendorCustomField) => field.fieldValue?.value;
    isCustomFieldsLoading = false;

    /**
     * Form enforces Lob API constraints for check printing compatibility.
     *
     * PO Box rules: If address1 contains PO Box, it must be ONLY the PO Box.
     * Address2 can then only be empty or PMB code. Combined address length
     * must not exceed 50 chars for Lob processing.
     */
    form: UntypedFormGroup = this._fb.group({
        name: [
            '',
            Validators.compose([
                Validators.required,
                Validators.maxLength(40), // must be <= 40 characters for lob check printing (https://docs.lob.com/#operation/address_create)
            ]),
        ],
        nickname: [''],
        accountNumber: [''],
        ein: [''],
        category: [
            null,
        ],
        address1: [
            '',
            [
                Validators.required,
                conditionalValidator(() => {
                    return this.containsPOBox.test(this.form.controls['address1'].value);
                }, customPatternValidator(/^(P\.?\s*O\.?\s+Box|post\s+office\s+box|p\s*o\s+box)\s\w+$/i, 'vendorAddress1ShouldContainPOBoxOnly')),
            ], // if contains po box, must only be po box number // could be done with regex validator, but that wouldnt return a user friendly error message
        ],
        address2: [
            '',
            [
                customPatternValidator(this.containsPOBox, 'vendorAddress2ShouldntContainPOBox', true),
                conditionalValidator(() => {
                    return this.containsPOBox.test(this.form.controls['address1'].value);
                }, customPatternValidator(/^(PMB.*)?$/i, 'vendorAddress2MustBePMBWhenAddress1ContainsPOBox')),
                (formControl: UntypedFormControl) => {
                    if (!formControl.parent) {
                        return null;
                    }

                    if (this.form.controls['address1'].value.length + this.form.controls['address2'].value.length > 50) {
                        return {vendorMaxAddressCombinedLength: true};
                    }

                    return null;
                },
            ],
        ], // if address1 contains po box, must be empty OR a PMB code // if this contains a po box, alert needs to be in address1
        city: [
            '',
            Validators.required,
        ],
        region: [
            '',
            Validators.required,
        ],
        zip: [
            '',
            Validators.required,
        ],
        hasLogo: [false],
        is1099Eligible: [false],
        phone: [this.vendor ? this.vendor.phone : ''],
        phoneExt: [this.vendor ? this.vendor.phoneExt : ''],
        fax: [this.vendor ? this.vendor.fax : ''],
        email: [this.vendor ? this.vendor.email : ''],
        contactName: [this.vendor ? this.vendor.contactName : ''],
        website: [this.vendor ? this.vendor.website : ''],
        mailStopCode: [
            this.vendor ? this.vendor.mailStopCode : '',
            Validators.maxLength(40),
        ],
        // ACH payment setup (Treasury orgs, new vendor only). These validators only ever run while
        // the user has opted in - the group is disabled whenever the enableAch toggle is off, see
        // syncAchEnablement().
        enableAch: [false],
        achDetails: this._fb.group({
            accountHolderName: [
                '',
                Validators.required,
            ],
            accountType: [
                'checking',
                Validators.required,
            ],
            routingNumber: [
                '',
                Validators.compose([Validators.required, Validators.minLength(9), Validators.maxLength(9)]),
            ],
            accountNumber: [
                '',
                Validators.compose([Validators.required, Validators.minLength(4), Validators.maxLength(17)]),
            ],
            confirmAccountNumber: [
                '',
                Validators.compose([
                    Validators.required,
                    this.accountNumberMatchValidator(),
                ]),
            ],
        }),
    });

    // state
    isOrgPrefLoading = true;
    isVendorLoading = true;
    isFormSubmitting = false;
    hasReadAccess = false;
    hasWriteAccess = false;
    hasAccrualAccounting = false;
    hasTreasuryEnabled = false;
    jwt: JwtLegFiClaims;
    returnRoute = '/app/vendors';
    returnLabel = 'vendor list';

    // image work
    isVendorLogoDeleteInFlight = false;
    vendorLogoBase64 = '';
    readonly vendorLogoAccept = 'image/png,image/jpeg,image/gif,image/webp';
    readonly vendorLogoHint = 'Recommended image size: 325 x 194px. Supported file types: PNG, JPG, GIF, WEBP.';
    isLogoDialogOpen = false;
    pickedLogoFile: File | null = null;

    readonly addressSuggestions = new AddressSuggestions(() => this._cdr.markForCheck());

    constructor(
            private _activatedRoute: ActivatedRoute,
            private _billPayVendorService: VendorService,
            private _fb: UntypedFormBuilder,
            private _growler: GrowlerService,
            private _organizationService: OrganizationService,
            private _router: Router,
            private _vendorBankAccountsService: VendorBankAccountsService,
            private _bankAccountInsights: BankAccountInsightsService,
            private _cdr: ChangeDetectorRef,
    ) {
    }

    /** ACH setup section is only offered for new vendors in Treasury orgs once the user opts in. */
    get isAchEnabled(): boolean {
        return this.form?.get('enableAch')?.value === true;
    }

    /** Save stays off until the mailing address checks are printed to is complete. */
    get hasAddress(): boolean {
        return ['address1', 'city', 'region', 'zip'].every((name) => this.form.get(name).valid);
    }

    /** A picked Google suggestion fills the street, unit, city, state and zip; anything Google lacks stays as typed. */
    onAddressPicked(row: AddressSuggestionRow | null): void {
        if (row?.label) {
            this.form.controls['address1'].setValue(row.label);
        }
        this.addressSuggestions.pick(row).pipe(untilDestroyed(this)).subscribe((address) => {
            if (!address) {
                return;
            }
            const patch: Record<string, unknown> = {};
            if (address.line1) {
                patch['address1'] = address.line1;
                patch['address2'] = address.line2;
            }
            if (address.city) {
                patch['city'] = address.city;
            }
            if (address.region) {
                patch['region'] = address.region;
            }
            if (address.zip) {
                patch['zip'] = address.zip;
            }
            this.form.patchValue(patch);
            this._cdr.markForCheck();
        });
    }

    /** Typed accessor for the nested ACH form group. */
    get achDetails(): UntypedFormGroup {
        return this.form.get('achDetails') as UntypedFormGroup;
    }

    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();
        this.hasReadAccess = LegFiJwtService.doesUserHaveModulePermission('billpay', false);
        this.hasWriteAccess = LegFiJwtService.doesUserHaveModulePermission('billpay', true);

        if (!this.embedded) {
            this.vendorId = this._activatedRoute.snapshot.params['id'] || 0;
        }

        this.setTreasuryEnabled();
        this.loadOrganization();

        if (this.vendorId) {
            this.returnRoute = '/app/vendor/details/' + this.vendorId;
            this.returnLabel = 'vendor detail';
            this.getVendor();
        } else {
            this.isVendorLoading = false;
        }

        // Every flow, not just the routed create flow: the ACH group must start disabled so it
        // cannot hold the form invalid on the edit and embedded quick-add flows, where the ACH
        // section never renders and the user has no toggle to release it.
        this.watchAchToggle();

        this.form.controls['address1'].valueChanges.pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.form.controls['address2'].markAsTouched();
                this.form.controls['address2'].updateValueAndValidity();
            },
        });
    }

    /**
     * ACH setup is offered for new vendors (routed flow, not the embedded quick-add dialog) to
     * admins/super-users in Treasury-enabled orgs. Gated on the stripe-treasury.enabled feature
     * flag ONLY (not KYC completion): adding an ACH bank account is not KYC-dependent - only money
     * movement to the vendor is (SD-7869). Sync check off the JWT, so no org round trip is needed.
     */
    private setTreasuryEnabled(): void {
        const isRoutedNewVendorAdmin = !this.vendorId && !this.embedded && !!(this.jwt?.admin || this.jwt?.superUser);
        this.hasTreasuryEnabled = isRoutedNewVendorAdmin && this._bankAccountInsights.hasTreasuryEnabled();
    }

    /** Load the organization for the accounting preference used by the vendor category flow. */
    private loadOrganization(): void {
        this._organizationService
                .getOrganizationById(null, ['preferences'])
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (organization: Organization) => {
                        this.hasAccrualAccounting = !organization.preferences?.useCashAccounting;
                        this.isOrgPrefLoading = false;
                        this._cdr.markForCheck();
                    },
                    error: () => {
                        this.isOrgPrefLoading = false;
                        this._cdr.markForCheck();
                    },
                });
    }

    /**
     * Keep the ACH group's participation in form validity in sync with the toggle, and keep the
     * confirm field in sync with the account number so the match validator stays accurate as the
     * user types.
     */
    private watchAchToggle(): void {
        this.syncAchEnablement();

        this.form.get('enableAch').valueChanges.pipe(untilDestroyed(this)).subscribe({
            next: () => this.syncAchEnablement(),
        });

        this.achDetails.get('accountNumber').valueChanges.pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.achDetails.get('confirmAccountNumber').updateValueAndValidity();
            },
        });
    }

    /**
     * The ACH sub-form participates in validation only while the user has opted in.
     *
     * This is enforced by enabling/disabling the group rather than by conditional validators
     * (SD-8137). The ACH inputs carry the `required` attribute, so Angular's RequiredValidator
     * directive attaches a second, unconditional validator while they are rendered - and when the
     * *ngIf tears them down, Angular strips that validator WITHOUT recomputing the control's status
     * (`cleanUpControl` discards `cleanUpValidators`' "updated" signal). The controls kept a stale
     * INVALID status, and `onSubmit`'s `form.valid` check then blocked vendor creation for anyone who
     * toggled ACH on and back off. Disabled controls are excluded from the parent's validity and
     * their validators do not run, so the staleness cannot leak.
     */
    private syncAchEnablement(): void {
        if (this.isAchEnabled) {
            this.achDetails.enable({emitEvent: false});
        } else {
            this.achDetails.disable({emitEvent: false});
        }
    }

    /** Validator ensuring confirmAccountNumber matches the entered accountNumber. */
    private accountNumberMatchValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const accountNumber = control.parent?.get('accountNumber');
            return control.value === accountNumber?.value ? null : {mismatch: true};
        };
    }

    /** Get vendor detail if we are editing */
    getVendor(): void {
        this._billPayVendorService
                .getOrgVendorDetails(null, this.vendorId)
                .pipe(untilDestroyed(this))
                .subscribe(
                        {
                            next: (billPayVendor: Vendor) => {
                                this.vendor = billPayVendor;
                                this.patchForm();
                                this.isVendorLoading = false;
                                this.loadCustomFields();
                                this._cdr.markForCheck();
                            },
                            error: () => {
                                this._growler.oops('There was a problem loading this vendor.');
                            },
                        },
                );
    }

    /** Set up a few values in the form from existing vendor data if it exists */
    patchForm() {
        this.form.patchValue(this.vendor);
        this.form.get('region').setValue(ConfigGeoRegions.findRegion({countryCode: 'US', region: this.vendor.state}));
    }

    /** Load org-level custom fields with vendor's values when editing */
    loadCustomFields(): void {
        if (!this.vendorId) {
            return;
        }
        this.isCustomFieldsLoading = true;
        this._billPayVendorService
                .getOrgVendorCustomFields(null, this.vendorId)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (customFields: VendorCustomField[]) => {
                        this.customFields = customFields;
                        this.isCustomFieldsLoading = false;
                        this._cdr.markForCheck();
                    },
                    error: () => {
                        this.isCustomFieldsLoading = false;
                        this._cdr.markForCheck();
                    },
                });
    }

    /** Shared code to reset and clear out logo stuff */
    resetVendorLogo() {
        this.form.get('hasLogo').setValue(false);
        this.vendorLogoBase64 = '';
        if (this.vendor) {
            this.vendor.vendorLogoUrl = '';
        }
    }

    get vendorLogoPreview(): string {
        if (this.vendorLogoBase64) {
            return `data:image/png;base64,${this.vendorLogoBase64}`;
        }
        return this.vendor?.vendorLogoUrl || '';
    }

    openLogoDialog(file: File | null = null): void {
        this.pickedLogoFile = file;
        this.isLogoDialogOpen = true;
    }

    closeLogoDialog(): void {
        this.isLogoDialogOpen = false;
        this.pickedLogoFile = null;
    }

    /** The picked image is sized; it goes to the DB with the vendor save */
    onVendorLogoPicked(dataUrl: string): void {
        const imageParts = dataUrl.split(',');
        if (imageParts.length === 2) {
            this.vendorLogoBase64 = imageParts[1];
        }
        this.closeLogoDialog();
    }

    removeVendorLogo(): void {
        if (this.vendorLogoBase64 || !this.vendor?.vendorLogoUrl) {
            this.resetVendorLogo();
        } else {
            this.deleteVendorLogo();
        }
    }

    /**
     * Maps vendor category to transaction owner types for accounting.
     *
     * Balance sheet accounts (asset/liability/equity) create corresponding
     * owner types, while expense categories default to Category type.
     * This determines how transactions post in the general ledger.
     */
    onSubmit() {
        if (this.isFormSubmitting) {
            return;
        }
        this.isFormSubmitting = true;

        this.form.markAllAsTouched();

        if (!this.form.valid) {
            this.isFormSubmitting = false;
            this._growler.oops('Please double check all required fields.');
            return;
        }

        const form = this.form.getRawValue();
        // ACH details are persisted via a separate endpoint after the vendor is created.
        delete form['enableAch'];
        delete form['achDetails'];
        if (form['category']) {
            switch (form['category'].accountType) {
                case 'asset':
                    form['default_transaction_owner_type'] = 'App\\Models\\Entities\\FixedAsset';
                    break;
                case 'liability':
                    form['default_transaction_owner_type'] = 'App\\Models\\Entities\\Liability';
                    break;
                case 'equity':
                    form['default_transaction_owner_type'] = 'App\\Models\\Entities\\Equity';
                    break;
                default:
                    form['default_transaction_owner_type'] = 'App\\Models\\Entities\\Category';
                    break;
            }
            form['default_transaction_owner_id'] = form['category'].id;
        } else {
            form['default_transaction_owner_type'] = null;
            form['default_transaction_owner_id'] = null;
        }

        const requestBody = {
            organizationId: this.jwt.orgId,
            ...form,
        };

        if (this.vendorLogoBase64 !== null) {
            requestBody['vendorLogo'] = this.vendorLogoBase64;
        }

        if (form['region']) {
            requestBody['state'] = form['region'].region;
            requestBody['country'] = form['region'].countryCode;
        }

        if (this.vendor) {
            this.updateVendor(requestBody);
        } else {
            this.createVendor(requestBody);
        }
    }

    /** Submit form to update vendor info */
    updateVendor(requestBody) {
        this._billPayVendorService.updateOrgVendor(null, this.vendor.id, requestBody)
                .pipe(untilDestroyed(this))
                .subscribe(
                        {
                            next: (vendor: Vendor) => {
                                this.saveCustomFields(vendor);
                            },
                            error: (err) => {
                                this.isFormSubmitting = false;
                                this._growler.fromErrorResponse(err);
                            },
                        },
                );
    }

    /** Save custom field values after vendor update */
    saveCustomFields(vendor: Vendor): void {
        const fieldsToSave = this.customFields
                .filter(field => field.fieldValue.value.trim() !== '')
                .map(field => ({
                    name: field.name,
                    value: field.fieldValue.value,
                }));

        if (this.customFields.length === 0) {
            this.completeVendorUpdate(vendor);
            return;
        }

        this._billPayVendorService
                .saveVendorCustomFields(null, this.vendor.id, {fields: fieldsToSave})
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (updatedVendor: Vendor) => {
                        this.completeVendorUpdate(updatedVendor);
                    },
                    error: () => {
                        this._growler.warning('Warning', 'Vendor saved, but custom fields may not have been updated.');
                        this.completeVendorUpdate(vendor);
                    },
                });
    }

    /** Handle post-save navigation/events */
    completeVendorUpdate(vendor: Vendor): void {
        this._growler.success('Success!', 'Vendor has been updated.');
        if (this.embedded) {
            this.saved.emit(vendor);
        } else {
            this._router.navigate([`/app/vendor/details/${this.vendorId}`]);
        }
    }

    /** Submit form to create vendor  */
    createVendor(requestBody) {
        this._billPayVendorService.createOrgVendor(null, requestBody)
                .pipe(untilDestroyed(this))
                .subscribe(
                        {
                            next: (vendor: Vendor) => {
                                if (this.hasTreasuryEnabled && this.isAchEnabled) {
                                    this.createVendorBankAccount(vendor);
                                } else {
                                    this._growler.success('Success!', 'A New vendor has been added.');
                                    this.completeVendorCreate(vendor);
                                }
                            },
                            error: () => {
                                this.isFormSubmitting = false;
                                this._growler.oops('There was a problem adding your vendor.');
                            },
                        },
                );
    }

    /** Persist ACH bank details for the freshly created vendor (Treasury orgs only). */
    createVendorBankAccount(vendor: Vendor): void {
        const ach = this.achDetails.getRawValue();
        const request: CreateVendorBankAccountRequest = {
            accountHolderName: ach.accountHolderName,
            accountType: ach.accountType,
            routingNumber: ach.routingNumber,
            accountNumber: ach.accountNumber,
        };

        this._vendorBankAccountsService.create(this.jwt.orgId, vendor.id, request)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: () => {
                        this._growler.success('Success!', 'A New vendor has been added with ACH enabled.');
                        this.completeVendorCreate(vendor);
                    },
                    error: () => {
                        this._growler.warning(
                                'Warning',
                                'Vendor created, but ACH setup could not be completed. You can add bank details from the vendor page.',
                        );
                        this.completeVendorCreate(vendor);
                    },
                });
    }

    /** Handle post-create navigation/events */
    completeVendorCreate(vendor: Vendor): void {
        if (this.embedded) {
            this.saved.emit(vendor);
        } else {
            this._router.navigate([`/app/vendor/details/${vendor.id}`]);
        }
    }

    /** If vendor exists with actual vendor logo, delete the vendor logo */
    deleteVendorLogo(): void {
        this.isVendorLogoDeleteInFlight = true;
        this._billPayVendorService
                .deleteOrgVendorLogo(this.vendor.id)
                .pipe(untilDestroyed(this))
                .subscribe(
                        {
                            next: () => {
                                this._growler.success(
                                        'Success!',
                                        'Vendor logo removed.',
                                );
                                this.resetVendorLogo();
                                this.isVendorLogoDeleteInFlight = false;
                            },
                            error: () => {
                                this._growler.oops('There was a problem deleting this logo.');
                                this.isVendorLogoDeleteInFlight = false;
                            },
                        },
                );
    }

    /** trackBy for the custom fields loop to avoid unnecessary DOM re-renders. */
    trackByCustomFieldId(index: number, field: VendorCustomField): number {
        return field.id;
    }

    /** Easy check to confirm if form control is valid / should show required message */
    isFieldInvalid(formControlName: string) {
        return this.form.get(formControlName).touched && this.form.get(formControlName).invalid;
    }

    /** They are cancelling create/edit; re-route accordingly */
    cancel() {
        this._router.navigate([this.returnRoute]);
    }
}
